import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  UserCheck, 
  Building2, 
  FileSpreadsheet, 
  Database, 
  Package, 
  FileText, 
  TestTube,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Target,
  FileSignature,
  BookTemplate
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import OfflineIndicator from './OfflineIndicator';

// Definição dos itens do menu com permissões - Paleta Médica Sóbria
const MENU_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/',
    tooltip: 'Visão geral do sistema',
    roles: ['administrador', 'medico', 'enfermeiro', 'farmaceutico', 'recepcao', 'auditor', 'secretaria', 'dentista'],
    color: 'text-[#0F766E]',
    bgHover: 'hover:bg-[#0F766E]/10'
  },
  {
    id: 'admin-dashboard',
    label: 'Admin',
    icon: FileSpreadsheet,
    path: '/admin-dashboard',
    tooltip: 'Estatísticas administrativas',
    roles: ['administrador', 'auditor'],
    color: 'text-[#1E3A8A]',
    bgHover: 'hover:bg-[#1E3A8A]/10'
  },
  {
    id: 'goals',
    label: 'Metas',
    icon: Target,
    path: '/goals',
    tooltip: 'Metas e métricas do sistema',
    roles: ['administrador', 'auditor'],
    color: 'text-[#8B5CF6]',
    bgHover: 'hover:bg-[#8B5CF6]/10'
  },
  {
    id: 'approve-users',
    label: 'Usuários',
    icon: UserCheck,
    path: '/approve-users',
    tooltip: 'Gerenciar aprovação de novos usuários',
    roles: ['administrador'],
    color: 'text-[#5FB3A2]',
    bgHover: 'hover:bg-[#5FB3A2]/10'
  },
  {
    id: 'manage-units',
    label: 'Unidades',
    icon: Building2,
    path: '/manage-units',
    tooltip: 'Gerenciar unidades de saúde',
    roles: ['administrador'],
    color: 'text-[#64748B]',
    bgHover: 'hover:bg-gray-100'
  },
  {
    id: 'reports',
    label: 'Relatórios',
    icon: FileSpreadsheet,
    path: '/reports',
    tooltip: 'Relatórios e exportações',
    roles: ['administrador'],
    color: 'text-[#0F766E]',
    bgHover: 'hover:bg-[#0F766E]/10'
  },
  {
    id: 'delivery-dates',
    label: 'Entregas',
    icon: Package,
    path: '/delivery-dates',
    tooltip: 'Controle de entregas de medicamentos',
    roles: ['administrador', 'medico', 'enfermeiro', 'farmaceutico', 'recepcao', 'auditor', 'secretaria', 'dentista'],
    color: 'text-[#D97706]',
    bgHover: 'hover:bg-[#D97706]/10'
  },
  {
    id: 'document-search',
    label: 'Busca',
    icon: FileSpreadsheet,
    path: '/document-search',
    tooltip: 'Buscar documentos e receitas',
    roles: ['administrador', 'medico', 'enfermeiro', 'farmaceutico', 'recepcao', 'auditor', 'secretaria', 'dentista'],
    color: 'text-[#0F766E]',
    bgHover: 'hover:bg-[#0F766E]/10'
  },
  {
    id: 'tenant-settings',
    label: 'Configurações',
    icon: Building2,
    path: '/tenant-settings',
    tooltip: 'Configurações da clínica',
    roles: ['administrador'],
    color: 'text-[#64748B]',
    bgHover: 'hover:bg-gray-100'
  },
  {
    id: 'audit-logs',
    label: 'Auditoria',
    icon: FileSpreadsheet,
    path: '/audit-logs',
    tooltip: 'Logs de auditoria e segurança',
    roles: ['administrador', 'auditor'],
    color: 'text-red-600',
    bgHover: 'hover:bg-red-50'
  }
];

// Itens de ação rápida (modais) - Paleta Médica Sóbria
const ACTION_ITEMS = [
  {
    id: 'quick-prescription',
    label: 'Receita Avulsa',
    icon: FileText,
    tooltip: 'Criar receita avulsa para impressão rápida',
    roles: ['administrador', 'medico', 'dentista'],
    color: 'text-[#0F766E]',
    bgHover: 'hover:bg-[#0F766E]/10',
    action: 'openQuickPrescription'
  },
  {
    id: 'prescription-templates',
    label: 'Receitas Modelo',
    icon: BookTemplate,
    tooltip: 'Gerenciar templates de receitas pré-cadastradas',
    roles: ['administrador', 'medico', 'dentista'],
    color: 'text-[#F59E0B]',
    bgHover: 'hover:bg-[#F59E0B]/10',
    action: 'openPrescriptionTemplates'
  },
  {
    id: 'exam-request',
    label: 'Exames',
    icon: TestTube,
    tooltip: 'Criar pedido de exames laboratoriais',
    roles: ['administrador', 'medico', 'dentista'],
    color: 'text-[#5FB3A2]',
    bgHover: 'hover:bg-[#5FB3A2]/10',
    action: 'openExamRequest'
  },
  {
    id: 'medical-documents',
    label: 'Documentos',
    icon: FileSignature,
    tooltip: 'Atestados, declarações, laudos e outros documentos',
    roles: ['administrador', 'medico', 'dentista'],
    color: 'text-[#8B5CF6]',
    bgHover: 'hover:bg-[#8B5CF6]/10',
    action: 'openMedicalDocuments'
  },
  {
    id: 'import-data',
    label: 'Importar',
    icon: Database,
    tooltip: 'Importar/exportar dados do sistema',
    roles: ['administrador'],
    color: 'text-[#1E3A8A]',
    bgHover: 'hover:bg-[#1E3A8A]/10',
    action: 'openDataImport'
  }
];

export default function Navbar({ 
  currentUser, 
  onLogout, 
  onOpenQuickPrescription, 
  onOpenExamRequest, 
  onOpenDataImport,
  onOpenMedicalDocuments,
  onOpenPrescriptionTemplates
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const userRole = currentUser?.role || '';
  const isMasterAdmin = currentUser?.is_master_admin || false;

  // Adicionar item do Master Admin se for master admin
  const masterAdminItem = {
    id: 'admin-master',
    label: 'Master Admin',
    icon: FileSpreadsheet,
    path: '/admin-master',
    tooltip: 'Painel do administrador master',
    roles: ['administrador'],
    color: 'text-purple-600',
    bgHover: 'hover:bg-purple-100'
  };

  // Filtrar itens baseado nas permissões do usuário
  let visibleMenuItems = MENU_ITEMS.filter(item => 
    item.roles.includes(userRole)
  );

  // Adicionar Master Admin no início se for master admin
  if (isMasterAdmin) {
    visibleMenuItems = [masterAdminItem, ...visibleMenuItems];
  }

  const visibleActionItems = ACTION_ITEMS.filter(item => 
    item.roles.includes(userRole)
  );

  // Handler para ações
  const handleAction = (actionId) => {
    setIsMobileMenuOpen(false);
    switch (actionId) {
      case 'openQuickPrescription':
        onOpenQuickPrescription?.();
        break;
      case 'openExamRequest':
        onOpenExamRequest?.();
        break;
      case 'openDataImport':
        onOpenDataImport?.();
        break;
      case 'openMedicalDocuments':
        onOpenMedicalDocuments?.();
        break;
      case 'openPrescriptionTemplates':
        onOpenPrescriptionTemplates?.();
        break;
      default:
        break;
    }
  };

  // Handler para navegação
  const handleNavigate = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  // Handler para logout
  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      toast.success('Logout realizado com sucesso');
    }
  };

  // Verificar se item está ativo
  const isActive = (path) => location.pathname === path;

  return (
    <TooltipProvider delayDuration={200}>
      <nav className="bg-white border-b border-[#D1D5DB] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo / Título */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div 
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => handleNavigate('/')}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_50c5aac3-6fba-43d7-bb53-da93d2f1d3d5/artifacts/5syieeve_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2011_42_12.png"
                  alt="Sistema de Receitas"
                  className="h-[72px] w-auto object-contain"
                  onError={(e) => {
                    console.error('Logo failed to load');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>

            {/* Menu Desktop */}
            <div className="hidden lg:flex items-center gap-1">
              {visibleMenuItems.map((item) => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNavigate(item.path)}
                      className={`
                        rounded-lg px-3 h-9 gap-2 transition-all duration-150
                        ${isActive(item.path) 
                          ? `bg-[#0F766E]/10 text-[#0F766E] font-medium` 
                          : `text-[#64748B] hover:text-[#1F2933] hover:bg-gray-100`
                        }
                      `}
                      data-testid={`nav-${item.id}`}
                    >
                      <item.icon className={`w-4 h-4 ${isActive(item.path) ? 'text-[#0F766E]' : ''}`} />
                      <span className="hidden xl:inline text-sm">{item.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-[#1F2933] text-white">
                    <p className="text-xs">{item.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              ))}

              {/* Separador */}
              {visibleActionItems.length > 0 && (
                <div className="h-5 w-px bg-[#D1D5DB] mx-2" />
              )}

              {/* Ações Rápidas */}
              {visibleActionItems.map((item) => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAction(item.action)}
                      className={`
                        rounded-lg px-3 h-9 gap-2 border-[#D1D5DB]
                        text-[#64748B] hover:text-[#0F766E] hover:border-[#0F766E]/30 hover:bg-[#0F766E]/5 transition-all duration-150
                      `}
                      data-testid={`nav-action-${item.id}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="hidden xl:inline text-sm">{item.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-[#1F2933] text-white">
                    <p className="text-xs">{item.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>

            {/* User Info + Offline Indicator + Logout (Desktop) */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Offline Indicator */}
              <OfflineIndicator />
              
              {currentUser && (
                <div className="text-right">
                  <p className="text-sm font-medium text-[#1F2933]">{currentUser.nome}</p>
                  <p className="text-xs text-[#64748B]">{currentUser.email}</p>
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    className="rounded-lg px-3 h-9 border-[#D1D5DB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#B91C1C]/30 hover:bg-[#B91C1C]/5"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Encerrar sessão</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Menu Hambúrguer (Mobile) */}
            <div className="lg:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="rounded-full"
                    data-testid="mobile-menu-trigger"
                  >
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0 border-r border-[#D1D5DB]">
                  <SheetHeader className="p-4 border-b border-[#D1D5DB]">
                    <SheetTitle className="flex items-center gap-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_50c5aac3-6fba-43d7-bb53-da93d2f1d3d5/artifacts/5syieeve_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2011_42_12.png"
                        alt="Sistema de Receitas"
                        className="h-10 w-auto object-contain"
                      />
                    </SheetTitle>
                  </SheetHeader>

                  {/* User Info Mobile */}
                  {currentUser && (
                    <div className="p-4 bg-[#F4F7F8] border-b border-[#D1D5DB]">
                      <p className="font-medium text-[#1F2933]">{currentUser.nome}</p>
                      <p className="text-sm text-[#64748B]">{currentUser.email}</p>
                      {currentUser.crm && (
                        <Badge className="mt-2 bg-[#0F766E]/10 text-[#0F766E] border-[#0F766E]/20">
                          CRM: {currentUser.crm}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Menu Items Mobile */}
                  <div className="p-2">
                    <p className="px-3 py-2 text-xs font-medium text-[#64748B] uppercase tracking-wider">
                      Navegação
                    </p>
                    {visibleMenuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleNavigate(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors
                          ${isActive(item.path) 
                            ? 'bg-[#0F766E]/10 text-[#0F766E]' 
                            : 'text-[#64748B] hover:bg-gray-100 hover:text-[#1F2933]'
                          }
                        `}
                        data-testid={`mobile-nav-${item.id}`}
                      >
                        <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-[#0F766E]' : ''}`} />
                        <span className="flex-1 font-medium text-sm">{item.label}</span>
                        {isActive(item.path) && (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Action Items Mobile */}
                  {visibleActionItems.length > 0 && (
                    <div className="p-2 border-t border-[#D1D5DB]">
                      <p className="px-3 py-2 text-xs font-medium text-[#64748B] uppercase tracking-wider">
                        Ações Rápidas
                      </p>
                      {visibleActionItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleAction(item.action)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors text-[#64748B] hover:bg-gray-100 hover:text-[#1F2933]"
                          data-testid={`mobile-action-${item.id}`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="flex-1 font-medium text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Logout Mobile */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#D1D5DB] bg-white">
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="w-full rounded-lg border-[#D1D5DB] text-[#B91C1C] hover:bg-[#B91C1C]/5 hover:border-[#B91C1C]/30"
                      data-testid="mobile-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Encerrar Sessão
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </TooltipProvider>
  );
}
